import { ref, readonly, type Ref, onUnmounted, getCurrentInstance } from 'vue'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH'

type RequestOptions = {
  body?: unknown
  params?: Record<string, string | number | boolean | null | undefined>
  headers?: Record<string, string>
  signal?: AbortSignal
  // Per-request deadline. Defaults to DEFAULT_TIMEOUT_MS; pass null to opt a
  // request out entirely (no known caller needs that today).
  timeoutMs?: number | null
}

// Visitor-side state at the moment a request died without a response — the only
// way to tell "our API is slow" from "their network dropped" or "they left the
// page" (a request torn down by unload fails exactly like a dropped socket).
type ConnectionState = {
  online: boolean
  connection?: string
  hidden: boolean
  unloading: boolean
}

type FetchError = {
  message: string
  status: number | null
  endpoint: string
  // timeout: our own deadline fired. network: no response at all (dropped
  // socket, offline, CORS, page unload). http: the server answered, but non-2xx
  // or with an unreadable body.
  kind: 'timeout' | 'network' | 'http'
  // This one request, send → failure. Callers that retry report the whole
  // operation's `elapsedMs` next to it.
  durationMs: number
  body?: unknown
  timedOut?: boolean
  stack?: string
} & Partial<ConnectionState>

// Not a fetch option (fetch ignores unknown init keys): tells the bugsnag
// integration's fetch patch not to report this request's failures, because
// every useFetch caller reports its own — with the context the patch lacks.
// The inline <head> script hardcodes this literal (it can't import); keep both
// equal.
export const CALLER_REPORTS_ERRORS = '__pipeCallerReports'

// Catch-all deadline so a hung socket (flaky mobile networks, in-app browser
// proxies) can never park a caller forever — awaited event-processor chains
// block navigation on these requests, so "eventually" must be bounded.
const DEFAULT_TIMEOUT_MS = 30_000

// pagehide fires for tab close, navigating away and entering the bfcache;
// pageshow clears it when a bfcache'd page comes back.
let pageUnloading = false
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageUnloading = true
  })
  window.addEventListener('pageshow', () => {
    pageUnloading = false
  })
}

function connectionState(): Partial<ConnectionState> {
  if (typeof window === 'undefined') return {}
  const nav = navigator as Navigator & { connection?: { effectiveType?: string } }
  return {
    online: nav.onLine,
    connection: nav.connection?.effectiveType,
    hidden: document.hidden,
    unloading: pageUnloading,
  }
}

type UseFetch = {
  loading: Readonly<Ref<boolean>>
  error: Readonly<Ref<FetchError | null>>
  get: <T>(url: string, options?: Omit<RequestOptions, 'body'>) => Promise<T>
  post: <T>(url: string, options?: RequestOptions) => Promise<T>
  put: <T>(url: string, options?: RequestOptions) => Promise<T>
  patch: <T>(url: string, options?: RequestOptions) => Promise<T>
  abort: () => void
}

function buildUrl(url: string, params?: RequestOptions['params']): string {
  if (!params) return url

  const filtered = Object.entries(params).filter(([, value]) => value != null) as [string, string][]
  if (!filtered.length) return url

  return `${url}?${new URLSearchParams(filtered).toString()}`
}

export function useFetch(baseUrl = ''): UseFetch {
  const loading = ref(false)
  const error = ref<FetchError | null>(null)
  let abortCurrent: (() => void) | null = null

  // Reset default state, when component dismounted, that uses this hook (composable).
  // Only register when called inside a setup() — outside (e.g. from a watch
  // callback after an await), getCurrentInstance() is null and Vue warns.
  // Callers in that scenario are responsible for invoking abort() themselves.
  if (getCurrentInstance()) {
    onUnmounted(() => {
      abortCurrent?.()
      loading.value = false
      error.value = null
    })
  }

  function abort() {
    abortCurrent?.()
  }

  async function request<T>(
    method: HttpMethod,
    url: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { body, params, headers = {}, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options

    // Chrome 64–65 have no AbortController, and the build's polyfills cover only
    // JavaScript itself: there a request cannot be cancelled, so an abort stops
    // the wait for it instead, failing it the way a cancelled fetch fails.
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null
    let stopWaiting: ((reason: unknown) => void) | undefined
    const waitStopped = ctrl
      ? null
      : new Promise<never>((_, reject) => {
          stopWaiting = reject
        })
    const settle = <R>(work: Promise<R>): Promise<R> =>
      waitStopped ? Promise.race([work, waitStopped]) : work
    const abortRequest = () => {
      if (ctrl) ctrl.abort()
      else stopWaiting?.(new DOMException('The request was aborted.', 'AbortError'))
    }
    // Keep a local handle: `abortCurrent` is shared state overwritten by every
    // request, so the timeout below must abort THIS request — aborting through
    // `abortCurrent` could kill a newer in-flight call.
    abortCurrent = abortRequest

    // Manual AbortController + setTimeout rather than AbortSignal.timeout()/
    // AbortSignal.any(): built pipes run in old Android WebViews and Meta
    // in-app browsers where those statics may not exist, and that cohort is
    // exactly where hung requests are most common.
    let timedOut = false
    const timer =
      timeoutMs != null
        ? setTimeout(() => {
            timedOut = true
            abortRequest()
          }, timeoutMs)
        : null
    // Forward an optional caller-supplied signal into ours (poor-man's
    // AbortSignal.any) so both the caller and the timeout can abort.
    if (signal) {
      if (signal.aborted) abortRequest()
      else signal.addEventListener('abort', abortRequest, { once: true })
    }

    loading.value = true
    error.value = null

    const fullUrl = buildUrl(`${baseUrl}${url}`, params)
    const startedAt = Date.now()
    let responded = false

    try {
      const init: RequestInit & Record<typeof CALLER_REPORTS_ERRORS, true> = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: ctrl?.signal,
        [CALLER_REPORTS_ERRORS]: true,
      }
      const response = await settle(fetch(fullUrl, init))
      responded = true

      if (!response.ok) {
        // Capture the response body so callers / console diagnostics can see
        // *why* the server rejected the request — `statusText` is empty over
        // HTTP/2, which is why error messages used to surface as "Request
        // failed: " with no detail.
        let parsedBody: unknown
        let bodyText = ''
        try {
          bodyText = await settle(response.text())
          parsedBody = bodyText ? JSON.parse(bodyText) : undefined
        } catch {
          parsedBody = bodyText || undefined
        }
        const summary =
          response.statusText ||
          (typeof parsedBody === 'object' && parsedBody !== null && 'message' in parsedBody
            ? String((parsedBody as { message: unknown }).message)
            : bodyText.slice(0, 200))
        const message = `Request failed (${response.status}): ${summary || 'no body'}`
        const err: FetchError = {
          message,
          status: response.status,
          endpoint: fullUrl,
          kind: 'http',
          durationMs: Date.now() - startedAt,
          body: parsedBody,
          stack: new Error(message).stack,
        }
        error.value = err

        throw err
      }

      // Handle empty responses (204 No Content etc.)
      const text = await settle(response.text())
      if (!text) {
        return null as unknown as T
      }

      return JSON.parse(text) as T
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // A deadline abort is a real failure the caller must see (it decides
        // whether to retry / proceed); only deliberate aborts (unmount,
        // caller signal) stay silent.
        if (timedOut) {
          error.value = {
            message: `Request timed out after ${timeoutMs}ms`,
            status: null,
            endpoint: fullUrl,
            kind: 'timeout',
            durationMs: Date.now() - startedAt,
            timedOut: true,
            stack: (err as Error).stack,
            ...connectionState(),
          }
          throw error.value
        }
        error.value = null

        throw err
      }

      if (!error.value) {
        error.value = {
          message: (err as Error)?.message ?? 'Unknown error',
          status: null,
          endpoint: fullUrl,
          // Past the response it's a body we couldn't read or parse — the
          // server did answer.
          kind: responded ? 'http' : 'network',
          durationMs: Date.now() - startedAt,
          stack: (err as Error)?.stack,
          ...(responded ? {} : connectionState()),
        }
      }

      throw error.value
    } finally {
      if (timer != null) clearTimeout(timer)
      loading.value = false
    }
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    get: (url, options) => request('GET', url, options),
    post: (url, options) => request('POST', url, options),
    put: (url, options) => request('PUT', url, options),
    patch: (url, options) => request('PATCH', url, options),
    abort,
  }
}
